import {Component, inject, OnInit, PLATFORM_ID} from '@angular/core';
import {Button} from 'primeng/button';
import {PrimeTemplate} from 'primeng/api';
import {Timeline} from 'primeng/timeline';
import {TimelineStep} from '../../../shared/model/timeline-step.model';
import {NavigatorDirective} from '../../../shared/directive/navigator.directive';
import {formComponentSignal} from '../../../shared/signal/common/form-component.signal';
import {RichResultsService} from '../../../shared/rich-results/rich-results.service';
import {buildHowToSection} from '../../../shared/rich-results/rich-results.builders';
import {RichResultsTypes} from '../../../shared/enum/rich-results-types.enum';
import {AnalyticsEventCategory, AnalyticsEventName} from '../../../shared/enum/gtm-cookie-consent.enum';
import {Theme} from '../../../shared/enum/theme.enum';
import {GoogleAnalyticsService} from '../../../shared/analytics/google/google-analytics.service';
import {PixelService} from '../../../shared/analytics/meta/pixel.service';
import {PixelEventName} from '../../../shared/analytics/meta/types/pixel.types';
import {MicrosoftClarityEvents} from '../../../shared/analytics/microsoft/types/microsoft-clarity-events.enum';
import {MicrosoftClarityService} from '../../../shared/analytics/microsoft/microsoft-clarity.service';
import {
  MicrosoftClaritySessionBumper
} from '../../../shared/analytics/microsoft/types/microsoft-clarity-session-bumper.enum';

@Component({
  selector: 'app-parking-steps',
  imports: [
    Button,
    PrimeTemplate,
    Timeline,
    NavigatorDirective
  ],
  templateUrl: './parking-steps.component.html',
  styleUrl: './parking-steps.component.scss'
})
export class ParkingStepsComponent implements OnInit {

  protected readonly platformId = inject(PLATFORM_ID);

  protected timelineSteps: TimelineStep[] = [
    {
      icon: 'pi pi-file-edit',
      title: $localize`:@@autopark.steps.step-1.title:Rozpocznij proces online`,
      description: $localize`:@@autopark.steps.step-1.description:Wypełnij prosty formularz kontaktowy podstawowymi informacjami i preferencjami. To zajmie tylko chwilę, a my natychmiast otrzymamy Twój wniosek i rozpoczniemy jego weryfikację.`,
      ctaText: $localize`:@@autopark.steps.step-1.ctaText:Przejdź do formularza`,
      ctaLink: '#formularz'
    },
    {
      icon: 'pi pi-comments',
      title: $localize`:@@autopark.steps.step-2.title:Konsultacja ekspercka`,
      description: $localize`:@@autopark.steps.step-2.description:Nasz doradca leasingowy skontaktuje się z Tobą w ciągu 24 godzin roboczych. Omówimy Twoje potrzeby, przedstawimy dostępne opcje flotowe i odpowiemy na wszystkie pytania. Razem wybierzemy najlepsze rozwiązanie dla Twojej firmy.`
    },
    {
      icon: 'pi pi-check-circle',
      title: $localize`:@@autopark.steps.step-3.title:Finalizacja i odbiór`,
      description: $localize`:@@autopark.steps.step-3.description:Po pozytywnej weryfikacji wniosku przygotujemy dokumenty do podpisu. Możesz podpisać umowę elektronicznie lub w naszym biurze. Po sfinalizowaniu formalności Twoja flota już znajduje się w odpowiednich rękach, na odpowiednim miejscu.`
    }
  ];

  protected readonly formComponentSignal = formComponentSignal;

  constructor(
    private readonly richResultsService: RichResultsService,
    private readonly googleAnalyticsService: GoogleAnalyticsService,
    private readonly pixelService: PixelService,
    private readonly microsoftClarityService: MicrosoftClarityService) {
  }

  ngOnInit(): void {
    this.constructSchema();
  }

  private constructSchema(): void {
    const howToSchema = buildHowToSection({
      name: 'Trzy proste kroki dzielą Cię od dopasowanego rozwiązania dla Twojej floty',
      itemListElement: this.timelineSteps.map((step, idx) => ({
        '@type': RichResultsTypes.ListItem,
        position: idx + 1,
        item: {
          '@type': RichResultsTypes.Thing,
          name: step.title,
          description: step.description
        }
      }))
    });

    this.richResultsService.register(howToSchema);
  }

  protected onCtaClick(): void {
    this.googleAnalyticsService.trackEvent({
      event: AnalyticsEventName.RequestCtaClick,
      eventCategory: AnalyticsEventCategory.CTA,
      eventPayload: Theme.PARKING
    });

    this.pixelService.trackEvent(PixelEventName.RequestCta, {
      page: Theme.PARKING
    });

    this.microsoftClarityService.trackEvent(
      MicrosoftClarityEvents.RequestCta
    );

    this.microsoftClarityService.upgradeSession(MicrosoftClaritySessionBumper.RequestCtaClicked)
  }
}
